import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api";
import { track } from "../../lib/analytics";
import { LabelPreview } from "./LabelPreview";
import { ColorSelect } from "./ColorSelect";
import {
  COLLAR_COLORS,
  COLLAR_PRICE_TIERS_CENTS,
  MINI_TAG_PRICE_TIERS_CENTS,
  productPriceCents,
  shippingCents,
  SHIPPING_OPTIONS,
  type ProductType,
  type ShippingMethod,
} from "@petloc/shared";

function euro(cents: number): string {
  return `${(cents / 100).toFixed(2).replace(".", ",")} €`;
}

interface Cat {
  id: string;
  name: string;
  species: "dog" | "cat" | "dragon";
  motto: string | null;
}

const SPECIES_LABEL: Record<Cat["species"], { emoji: string; key: string }> = {
  dog: { emoji: "🐕", key: "cats.speciesDog" },
  cat: { emoji: "🐈", key: "cats.speciesCat" },
  dragon: { emoji: "🐉", key: "cats.speciesDragon" },
};

interface Me {
  name?: string;
  street: string;
  zip: string;
  city: string;
  country: string;
}

type CollarColor = typeof COLLAR_COLORS[number];

export function OrderCollarsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const cat = useQuery({ queryKey: ["cat", id], queryFn: () => api<Cat>(`/api/pets/${id}`) });
  const meQ = useQuery({
    queryKey: ["me-address"],
    queryFn: () => api<{ user: Me & { role?: string }; testCheckout?: boolean }>("/api/me"),
  });
  const canTestCheckout = !!meQ.data?.testCheckout;

  const [productType, setProductType] = useState<ProductType>("collar");
  const [colors, setColors] = useState<CollarColor[]>(["red"]);
  const [showName, setShowName] = useState(true);
  const [shippingMethod, setShippingMethod] = useState<ShippingMethod>("brief");
  const [shipping, setShipping] = useState<Me>({ name: "", street: "", zip: "", city: "", country: "DE" });
  const [busy, setBusy] = useState<null | "checkout" | "test">(null);
  const [error, setError] = useState<string | null>(null);
  const isTag = productType === "tag";
  const productNounPl = isTag ? "Anhänger" : "Halsbänder";

  useEffect(() => {
    if (meQ.data?.user) setShipping(meQ.data.user);
  }, [meQ.data]);

  const setQuantity = (qty: number) => {
    if (qty < 1 || qty > 10) return;
    if (qty > colors.length) {
      setColors([...colors, ...Array.from({ length: qty - colors.length }, () => "blue" as CollarColor)]);
    } else {
      setColors(colors.slice(0, qty));
    }
  };
  const updateColor = (i: number, color: CollarColor) =>
    setColors(colors.map((c, idx) => (idx === i ? color : c)));

  const tiers = isTag ? MINI_TAG_PRICE_TIERS_CENTS : COLLAR_PRICE_TIERS_CENTS;
  const itemCents = productPriceCents(productType, colors.length);
  const shipCents = shippingCents(shippingMethod);
  const priceCents = itemCents + shipCents;
  const singlePriceCents = tiers[0]!;
  const savedCents = colors.length * singlePriceCents - itemCents;

  const orderBody = () => ({
    catId: id,
    productType,
    shippingMethod,
    collars: colors.map((color) => (isTag ? { showName } : { color, showName })),
    shippingAddress: shipping,
  });

  const errorMessage = (e: unknown): string => {
    if (e instanceof ApiError) {
      if (e.status === 401) return "Bitte melde dich erneut an und versuche es noch einmal.";
      if (e.status === 400)
        return "Die Bestellung konnte nicht gestartet werden. Bitte prüfe deine Angaben und versuche es erneut.";
      return `Es ist ein Fehler aufgetreten (Code ${e.status}). Bitte versuche es später erneut.`;
    }
    return "Verbindung fehlgeschlagen. Bitte prüfe deine Internetverbindung und versuche es erneut.";
  };

  const startCheckout = async (kind: "checkout" | "test") => {
    if (busy) return;
    setError(null);
    setBusy(kind);
    if (kind === "checkout") {
      track("order_started", { product: productType, quantity: colors.length, priceCents, shipping: shippingMethod });
    }
    try {
      const path = kind === "test" ? "/api/orders/test-checkout" : "/api/orders/checkout";
      const res = await api<{ url: string }>(path, {
        method: "POST",
        body: JSON.stringify(orderBody()),
      });
      window.location.href = res.url;
    } catch (e) {
      setError(errorMessage(e));
      setBusy(null);
    }
  };

  if (!cat.data) return null;

  return (
    <div className="max-w-4xl">
      <Link to={`/app/pets/${id}`} className="text-sm text-slate-500">← {t("common.back")}</Link>
      <h1 className="font-display text-3xl font-semibold tracking-tight mt-2 mb-1">
        {t("cats.orderCollarsFor", { name: cat.data.name })}
      </h1>
      <div className="mb-6 text-sm text-slate-500">
        {t("cats.species")}:{" "}
        <span className="font-medium text-slate-700">
          {SPECIES_LABEL[cat.data.species].emoji} {t(SPECIES_LABEL[cat.data.species].key)}
        </span>{" "}
        — das berücksichtigen wir bei der Auswahl.
      </div>

      {/* Produktwahl: Halsband vs. Mini-Anhänger */}
      <div className="card p-5 mb-6">
        <div className="text-sm font-semibold mb-3 text-slate-700">Was möchtest du bestellen?</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setProductType("collar")}
            className={`overflow-hidden rounded-xl border text-left transition-colors ${
              !isTag ? "border-brand-500 ring-1 ring-brand-500" : "border-slate-200 hover:border-brand-300"
            }`}
          >
            <img src="/produkt-halsband.jpg" alt="petloc QR-Halsband" className="h-28 w-full object-cover" />
            <div className="p-3">
              <div className="font-semibold text-slate-900">Halsband</div>
              <div className="text-xs text-slate-500 mt-0.5">Sicherheitshalsband mit QR — ab 15 €</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setProductType("tag")}
            className={`overflow-hidden rounded-xl border text-left transition-colors ${
              isTag ? "border-brand-500 ring-1 ring-brand-500" : "border-slate-200 hover:border-brand-300"
            }`}
          >
            <img src="/produkt-anhaenger.jpg" alt="petloc Mini-Anhänger mit QR" className="h-28 w-full object-cover" />
            <div className="p-3">
              <div className="font-semibold text-slate-900">Mini-Anhänger</div>
              <div className="text-xs text-slate-500 mt-0.5">Fürs vorhandene Halsband — ab 10 €</div>
            </div>
          </button>
        </div>
        {isTag && (
          <p className="mt-3 rounded-lg border border-brand-100 bg-brand-50 p-3 text-sm text-slate-700">
            Dein Tier hat schon ein Halsband? Dann bestell nur den Mini-Anhänger mit QR-Code: leicht,
            klein und einfach am vorhandenen Halsband befestigt. Gleicher Schutz, ohne neues Halsband.
          </p>
        )}
        <p className="mt-3 text-xs text-slate-500">
          Jedes Stück hat einen eigenen QR-Code und ist einzeln sperrbar, falls es verloren geht — die
          anderen bleiben aktiv.
        </p>
      </div>

      <div className="card p-5 mb-6">
        <div className="text-sm font-semibold mb-3 text-slate-700">{t("orders.tiersTitle")}</div>
        <div className="grid grid-cols-10 gap-1 text-center text-xs">
          {tiers.map((priceC, idx) => {
            const qty = idx + 1;
            const isSelected = qty === colors.length;
            const perPiece = priceC / qty;
            return (
              <button
                key={qty}
                type="button"
                onClick={() => setQuantity(qty)}
                className={`rounded-lg border p-2 transition-all ${
                  isSelected
                    ? "bg-brand-600 text-white border-brand-600 shadow-sm"
                    : "bg-white text-slate-700 border-slate-200 hover:border-brand-400"
                }`}
              >
                <div className="font-bold text-sm">{qty} Stück</div>
                <div className={`mt-1 ${isSelected ? "opacity-95" : "text-slate-900 font-medium"}`}>
                  {euro(priceC)}
                </div>
                <div className={`text-[10px] mt-0.5 ${isSelected ? "opacity-80" : "text-slate-500"}`}>
                  {euro(perPiece)}/{t("orders.perPiece").split(" ")[0]}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Ab 2 {productNounPl} zahlst du weniger pro Stück — je mehr, desto günstiger.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card p-6">
          {/* One-time beschriftung setting for the whole order */}
          <div className="label">Beschriftung (für alle {productNounPl})</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={showName} onChange={(e) => setShowName(e.target.checked)} />
              <span>Name „{cat.data.name}" aufdrucken</span>
            </label>
          </div>

          {!isTag && (
            <>
              <hr className="my-5" />
              <div className="label mb-3">{t("orders.quantity")}</div>
              <div className="space-y-3">
                {colors.map((color, i) => (
                  <div key={i} className="flex items-center gap-3 border border-slate-200 rounded-lg p-3">
                    <span className="text-xs text-slate-500 w-6 shrink-0">#{i + 1}</span>
                    <ColorSelect
                      value={color}
                      onChange={(c) => updateColor(i, c)}
                      className="flex-1 max-w-[14rem]"
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2">Anzahl oben in der Staffel ändern.</p>
            </>
          )}

          <hr className="my-6" />

          <div className="text-base font-medium mb-3">Lieferadresse</div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3">
              <label className="label">Name</label>
              <input className="input" value={shipping.name ?? ""} onChange={(e) => setShipping({ ...shipping, name: e.target.value })} />
            </div>
            <div className="col-span-3">
              <label className="label">Straße</label>
              <input className="input" value={shipping.street} onChange={(e) => setShipping({ ...shipping, street: e.target.value })} />
            </div>
            <div>
              <label className="label">PLZ</label>
              <input className="input" value={shipping.zip} onChange={(e) => setShipping({ ...shipping, zip: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="label">Ort</label>
              <input className="input" value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} />
            </div>
          </div>

          <hr className="my-6" />

          <div className="text-base font-medium mb-3">Versand</div>
          <div className="space-y-2">
            {(["brief", "paket"] as ShippingMethod[]).map((m) => (
              <label
                key={m}
                className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-3 text-sm ${
                  shippingMethod === m ? "border-brand-500 bg-brand-50" : "border-slate-200 hover:border-brand-300"
                }`}
              >
                <span className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="shipping"
                    checked={shippingMethod === m}
                    onChange={() => setShippingMethod(m)}
                  />
                  <span>{m === "paket" ? "Paket (versichert)" : "Brief (unversichert)"}</span>
                </span>
                <span className="font-medium">+ {euro(SHIPPING_OPTIONS[m].cents)}</span>
              </label>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm text-slate-500">{t("orders.total")}</div>
              <div className="text-2xl font-semibold">{euro(priceCents)}</div>
              <div className="text-xs text-slate-500">
                {euro(itemCents / colors.length)} {t("orders.perPiece")} · inkl. Versand {euro(shipCents)}
              </div>
              {savedCents > 0 && (
                <div className="text-xs text-emerald-700 font-medium mt-1">
                  {t("orders.savings", { amount: euro(savedCents) })}
                </div>
              )}
              <div className="text-xs text-slate-400 mt-1">inkl. 19 % MwSt im Bezahlvorgang</div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <button
                type="button"
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => startCheckout("checkout")}
                disabled={busy !== null}
              >
                {busy === "checkout" ? "Weiterleitung zur Zahlung…" : t("orders.checkout")}
              </button>
              {canTestCheckout && (
                <button
                  type="button"
                  onClick={() => startCheckout("test")}
                  disabled={busy !== null}
                  className="text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700 disabled:opacity-60"
                >
                  {busy === "test" ? "Wird angelegt…" : "Testbestellung (ohne Zahlung)"}
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="card p-6">
          <div className="label">{t("orders.preview")}</div>
          <LabelPreview
            catId={cat.data.id}
            name={cat.data.name}
            showName={showName}
          />
          <p className="mt-3 text-xs text-slate-500">
            Dies ist eine Vorschau. Das gedruckte {isTag ? "Etikett" : "Halsband"} kann leicht abweichen.
          </p>
        </div>
      </div>
    </div>
  );
}
